import React, { useState, useEffect } from 'react';
import { enhancedApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Pagination } from '../components/Pagination';
import { 
  Users, 
  Calendar, 
  Clock, 
  AlertCircle, 
  CheckCircle, 
  Eye, 
  Filter,
  Search,
  RefreshCw,
  User,
  UserCheck,
  TrendingUp,
  TrendingDown,
  Activity,
  X,
  Trash2
} from 'lucide-react';

interface Assignment {
  id: number;
  case_type: 'mortality' | 'morbidity';
  case_id: number;
  assignment_type: 'individual' | 'team' | 'multi_individual';
  multi_auditor_support: boolean;
  assigned_to_id: number;
  assigned_to_name: string;
  assigned_to_role: string;
  primary_auditor_id?: number;
  supervisor_id?: number;
  reviewer_name?: string;
  reviewer_role?: string;
  assigned_by: number;
  assigned_by_name: string;
  assigned_date: string;
  due_date: string;
  status: 'assigned' | 'in_progress' | 'submitted' | 'under_review' | 'approved' | 'rejected';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  notes?: string;
  progress_percentage?: number;
  // Patient info
  patient_id?: string;
  age?: string;
  gender?: string;
  // Case-specific info
  cause_of_death?: string;
  case_summary?: string;
  // Multi-auditor fields
  all_auditor_names?: string;
  auditor_count?: number;
  // Form record tracking
  has_form_record?: boolean;
}

export const AssignmentDashboard: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);

  useEffect(() => {
    fetchAssignments();
  }, []);

  // Refetch assignments when filters change
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
    fetchAssignments();
  }, [statusFilter, priorityFilter, typeFilter, searchTerm]);

  // Refetch assignments when pagination changes
  useEffect(() => {
    fetchAssignments();
  }, [currentPage, itemsPerPage]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params: any = {
        page: currentPage,
        limit: itemsPerPage
      };
      
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      if (priorityFilter && priorityFilter !== 'all') {
        params.priority = priorityFilter;
      }
      
      if (typeFilter && typeFilter !== 'all') {
        params.type = typeFilter;
      }
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      const response = await enhancedApi.assignments.getAssignments(params);
      
      if (response.success) {
        setAssignments(response.data || []);
        
        // Update pagination info
        const responseWithPagination = response as any;
        if (responseWithPagination.pagination) {
          setTotalPages(responseWithPagination.pagination.pages);
          setTotalItems(responseWithPagination.pagination.total);
        }
      } else {
        console.error('Failed to fetch assignments:', response.message);
        setAssignments([]);
      }
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      assigned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      submitted: 'bg-purple-100 text-purple-800',
      under_review: 'bg-orange-100 text-orange-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return statusClasses[status as keyof typeof statusClasses] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority: string) => {
    const priorityClasses = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return priorityClasses[priority as keyof typeof priorityClasses] || 'bg-gray-100 text-gray-800';
  };

  const getDaysUntilDue = (dueDate: string) => {
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getOverdueStatus = (dueDate: string, status: string) => {
    const daysUntilDue = getDaysUntilDue(dueDate);
    if (status === 'approved' || status === 'rejected') return null;
    if (daysUntilDue < 0) return 'overdue';
    if (daysUntilDue <= 2) return 'urgent';
    return null;
  };

  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = assignment.assigned_to_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assignment.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || assignment.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || assignment.priority === priorityFilter;
    const matchesType = typeFilter === 'all' || assignment.assignment_type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesPriority && matchesType;
  });

  const getStats = () => {
    const total = assignments.length;
    const inProgress = assignments.filter(a => a.status === 'in_progress').length;
    const overdue = assignments.filter(a => getOverdueStatus(a.due_date, a.status) === 'overdue').length;
    const completed = assignments.filter(a => a.status === 'approved').length;
    
    return { total, inProgress, overdue, completed };
  };

  // Handler functions for action buttons
  const handleViewAssignment = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setShowViewModal(true);
  };

  const handleDeleteAssignment = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setShowDeleteModal(true);
  };

  const confirmDeleteAssignment = async () => {
    if (!selectedAssignment) return;
    
    try {
      const response = await enhancedApi.assignments.deleteAssignment(selectedAssignment.id);
      if (response.success) {
        // Remove from local state
        setAssignments(assignments.filter(a => a.id !== selectedAssignment.id));
        setShowDeleteModal(false);
        setSelectedAssignment(null);
      } else {
        addToast({
          type: 'error',
          title: 'Error',
          message: 'Failed to delete assignment: ' + response.message
        });
      }
    } catch (error) {
      console.error('Failed to delete assignment:', error);
      addToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to delete assignment'
      });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setItemsPerPage(itemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleUpdateAssignment = async (updatedAssignment: Assignment) => {
    try {
      setLoading(true);
      
      // Call the backend API to update the assignment
      const response = await enhancedApi.assignments.updateAssignment({
        id: updatedAssignment.id,
        status: updatedAssignment.status,
        priority: updatedAssignment.priority,
        due_date: updatedAssignment.due_date,
        notes: updatedAssignment.notes
      });
      
      if (response.success) {
        // Update the local state
        setAssignments(prev => 
          prev.map(a => a.id === updatedAssignment.id ? updatedAssignment : a)
        );
        
        setSelectedAssignment(null);
        addToast({
          type: 'success',
          title: 'Success',
          message: 'Assignment updated successfully'
        });
      } else {
        addToast({
          type: 'error',
          title: 'Error',
          message: 'Failed to update assignment: ' + response.message
        });
      }
    } catch (error) {
      console.error('Error updating assignment:', error);
      addToast({
        type: 'error',
        title: 'Error',
        message: 'Error updating assignment. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const stats = getStats();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Assignment Dashboard</h1>
              <p className="text-blue-100">Monitor and manage audit assignments</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Assignments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Activity className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.inProgress}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Overdue</p>
              <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search assignments..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="submitted">Submitted</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="individual">Individual</option>
              <option value="team">Team</option>
            </select>
            
            <button
              onClick={fetchAssignments}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Assignments Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Audit Assignments</h3>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600">Loading assignments...</span>
            </div>
          </div>
        ) : filteredAssignments.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No assignments found</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAssignments.map((assignment) => {
                  const overdueStatus = getOverdueStatus(assignment.due_date, assignment.status);
                  const daysUntilDue = getDaysUntilDue(assignment.due_date);
                  
                  return (
                    <tr key={assignment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full mr-3 ${
                            assignment.case_type === 'mortality' ? 'bg-red-500' : 'bg-blue-500'
                          }`}></div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {assignment.case_type === 'mortality' ? 'Mortality' : 'Morbidity'} Case #{assignment.case_id}
                            </div>
                            <div className="text-sm text-gray-500">
                              {assignment.assignment_type === 'individual' ? (
                                <span className="flex items-center">
                                  <User className="h-3 w-3 mr-1" />
                                  Individual
                                </span>
                              ) : (
                                <span className="flex items-center">
                                  <UserCheck className="h-3 w-3 mr-1" />
                                  Team
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {assignment.multi_auditor_support && assignment.all_auditor_names 
                            ? assignment.all_auditor_names
                            : assignment.assigned_to_name
                          }
                        </div>
                        <div className="text-sm text-gray-500">
                          {assignment.assignment_type === 'individual' ? 'Individual Assignment' : 
                           assignment.assignment_type === 'team' ? 'Team Assignment' : 
                           `Multi-Individual (${assignment.auditor_count || 0} auditors)`}
                        </div>
                        {assignment.reviewer_name && (
                          <div className="text-sm text-gray-500">
                            Supervisor: {assignment.reviewer_name}
                          </div>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(assignment.status)}`}>
                          {assignment.status.replace('_', ' ')}
                        </span>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${assignment.progress_percentage || 0}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-600">
                            {assignment.progress_percentage || 0}%
                          </span>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm text-gray-900">
                              {new Date(assignment.due_date).toLocaleDateString()}
                            </div>
                            {overdueStatus && (
                              <div className={`text-xs ${
                                overdueStatus === 'overdue' ? 'text-red-600' : 'text-orange-600'
                              }`}>
                                {overdueStatus === 'overdue' 
                                  ? `${Math.abs(daysUntilDue)} days overdue`
                                  : `${daysUntilDue} days left`
                                }
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadge(assignment.priority)}`}>
                          {assignment.priority}
                        </span>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleViewAssignment(assignment)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                            title="View Assignment Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {user?.role === 'superuser' && !assignment.has_form_record && (
                            <button 
                              onClick={() => handleDeleteAssignment(assignment)}
                              className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                              title="Delete Assignment"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {assignments.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            showItemsPerPage={true}
            itemsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      {/* View Assignment Modal */}
      {showViewModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Assignment Details</h3>
              <button 
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assignment ID</label>
                  <p className="text-gray-900">{selectedAssignment.id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Case Type</label>
                  <p className="text-gray-900 capitalize">{selectedAssignment.case_type}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID</label>
                  <p className="text-gray-900">{selectedAssignment.patient_id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age & Gender</label>
                  <p className="text-gray-900">{selectedAssignment.age}, {selectedAssignment.gender}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Type</label>
                  <p className="text-gray-900 capitalize">{selectedAssignment.assignment_type.replace('_', ' ')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                  <p className="text-gray-900">{selectedAssignment.assigned_to_name} ({selectedAssignment.assigned_to_role})</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reviewer</label>
                  <p className="text-gray-900">{selectedAssignment.reviewer_name} ({selectedAssignment.reviewer_role})</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned By</label>
                  <p className="text-gray-900">{selectedAssignment.assigned_by_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Date</label>
                  <p className="text-gray-900">{new Date(selectedAssignment.assigned_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <p className="text-gray-900">{new Date(selectedAssignment.due_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <div className="mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(selectedAssignment.status)}`}>
                      {selectedAssignment.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <div className="mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadge(selectedAssignment.priority)}`}>
                      {selectedAssignment.priority}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Progress</label>
                  <div className="flex items-center">
                    <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${selectedAssignment.progress_percentage || 0}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600">
                      {selectedAssignment.progress_percentage || 0}%
                    </span>
                  </div>
                </div>
              </div>
              
              {selectedAssignment.multi_auditor_support && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Auditors ({selectedAssignment.auditor_count})</label>
                  <p className="text-gray-900">{selectedAssignment.all_auditor_names}</p>
                </div>
              )}
              
              {selectedAssignment.case_type === 'mortality' && selectedAssignment.cause_of_death && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cause of Death</label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900 whitespace-pre-wrap">{selectedAssignment.cause_of_death}</p>
                  </div>
                </div>
              )}
              
              {selectedAssignment.case_type === 'morbidity' && selectedAssignment.case_summary && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Case Summary</label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900 whitespace-pre-wrap">{selectedAssignment.case_summary}</p>
                  </div>
                </div>
              )}
              
              {selectedAssignment.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900 whitespace-pre-wrap">{selectedAssignment.notes}</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button 
                onClick={() => setShowViewModal(false)}
                className="btn btn-outline"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Assignment</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700">
                Are you sure you want to delete this assignment for <strong>{selectedAssignment.assigned_to_name}</strong>?
              </p>
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Case:</strong> {selectedAssignment.case_type} - {selectedAssignment.patient_id}<br/>
                  <strong>Due Date:</strong> {new Date(selectedAssignment.due_date).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDeleteAssignment}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-2 inline" />
                Delete Assignment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
